/**
 * Inline stroke icons. Kept as paths rather than an icon font so the bundle has
 * no external dependency and every glyph inherits currentColor.
 */
export type IconName =
  | 'arrow-left'
  | 'layers'
  | 'cloud'
  | 'calendar'
  | 'clipboard'
  | 'grid'
  | 'pin'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'plus'
  | 'minus'
  | 'split'
  | 'ruler'
  | 'crosshair'
  | 'download'
  | 'contrast'
  | 'expand'
  | 'chart'
  | 'leaf'
  | 'cube'
  | 'alert'
  | 'globe'
  | 'check'
  | 'upload'
  | 'users'
  | 'info'
  | 'shield'
  | 'sign-out'
  | 'wheat';

const PATHS: Record<IconName, string> = {
  'arrow-left': 'M15 5 8 12l7 7',
  layers: 'M12 3 3 8l9 5 9-5-9-5ZM3 13l9 5 9-5M3 17.5l9 5 9-5',
  cloud: 'M7 18h9.5a3.5 3.5 0 0 0 .3-7A5.5 5.5 0 0 0 6.5 11 3.5 3.5 0 0 0 7 18Z',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  clipboard: 'M9 4h6v3H9zM7 5H5v16h14V5h-2M9 12h6M9 16h4',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  pin: 'M12 21s6-5.7 6-10a6 6 0 1 0-12 0c0 4.3 6 10 6 10ZM12 11h.01',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-left': 'm14 6-6 6 6 6',
  'chevron-right': 'm10 6 6 6-6 6',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  split: 'M12 3v18M7 8 3 12l4 4M17 8l4 4-4 4',
  ruler: 'M3 15 15 3l6 6L9 21zM7 11l2 2M11 7l2 2M15 11l2 2',
  crosshair: 'M12 3v3M12 18v3M3 12h3M18 12h3M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  download: 'M12 4v11M8 11l4 4 4-4M5 19h14',
  contrast: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 3v18',
  expand: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  chart: 'M4 20V6M4 20h16M8 20v-6M12 20v-9M16 20v-4',
  leaf: 'M5 19C5 11 11 5 20 5c0 9-6 14-14 14H5ZM5 19c3-4 6-6 10-8',
  cube: 'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3ZM12 12l8-4.5M12 12v9M12 12 4 7.5',
  alert: 'M12 4 2.5 20h19L12 4ZM12 10v4M12 17h.01',
  globe: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM3.5 9h17M3.5 15h17M12 3c-2.5 2.4-3.8 5.4-3.8 9s1.3 6.6 3.8 9c2.5-2.4 3.8-5.4 3.8-9S14.5 5.4 12 3Z',
  check: 'm5 12.5 4.5 4.5L19 7.5',
  upload: 'M12 19V8M8 12l4-4 4 4M5 4h14',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 20c0-3.6 2.9-5.5 6.5-5.5s6.5 1.9 6.5 5.5M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.8c2.1.6 3.5 2.2 3.5 5.2',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 11v6M12 7.5h.01',
  shield: 'M12 3 4.5 6v6c0 4.4 3.1 7.9 7.5 9 4.4-1.1 7.5-4.6 7.5-9V6L12 3Z',
  'sign-out': 'M15 5H5v14h10M18 12H9M15 9l3 3-3 3',
  wheat: 'M12 21V9M12 9c0-2.5 1.6-4.5 4-5 .4 2.6-1 4.7-4 5ZM12 9C12 6.5 10.4 4.5 8 4c-.4 2.6 1 4.7 4 5ZM12 15c0-2.5 1.6-4.5 4-5 .4 2.6-1 4.7-4 5ZM12 15c0-2.5-1.6-4.5-4-5-.4 2.6 1 4.7 4 5Z',
};

interface Props {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 18, className }: Props): JSX.Element {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
